import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import ReactSelect from 'react-select';
import vendorService from '../../services/VendorService';
import { useFetchQboAccounts } from '../MyTime/hooks/useFetchQboAccounts';

const AUD = { value: 'AUD', name: 'Australian Dollar' };
const refOption = (reference) => ({ value: String(reference.value), label: reference.name });
const selectedOption = (options, reference) =>
  options.find((option) => String(option.value) === String(reference?.value || '')) || null;
const optionToRef = (option) => (option ? { value: String(option.value), name: option.label } : null);

const CreateVendorDialog = ({ open, initialName = '', onCancel, onSave }) => {
  const { qboAccounts, qboAccountLoading, qboAccountError } = useFetchQboAccounts();
  const [terms, setTerms] = useState([]);
  const [termsLoading, setTermsLoading] = useState(false);
  const [termsError, setTermsError] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    Title: '',
    GivenName: '',
    MiddleName: '',
    FamilyName: '',
    Suffix: '',
    DisplayName: initialName,
    CompanyName: '',
    Email: '',
    Phone: '',
    Mobile: '',
    Website: '',
    CurrencyRef: AUD,
    Line1: '',
    City: '',
    CountrySubDivisionCode: '',
    PostalCode: '',
    Country: 'Australia',
    SalesTaxRegistered: false,
    TaxIdentifier: '',
    BillRate: '',
    TermRef: null,
    AcctNum: '',
    APAccountRef: null
  });

  useEffect(() => {
    let active = true;
    const fetchTerms = async () => {
      setTermsLoading(true);
      setTermsError('');
      try {
        const response = await vendorService.getTermsFromQBO();
        const qboTerms = response?.data?.data?.QueryResponse?.Term || [];
        if (active) {
          setTerms(
            qboTerms
              .filter((term) => term.Active !== false && term.Id && term.Name)
              .map((term) => ({ value: String(term.Id), label: term.Name }))
          );
        }
      } catch (error) {
        if (active) {
          setTermsError(error?.response?.data?.message || error?.message || 'Could not load QBO terms');
        }
      } finally {
        if (active) setTermsLoading(false);
      }
    };
    if (open) fetchTerms();
    return () => {
      active = false;
    };
  }, [open]);

  const accountOptions = useMemo(() => qboAccounts.map(refOption), [qboAccounts]);
  const selectProps = {
    isClearable: true,
    isSearchable: true,
    // eslint-disable-next-line no-undef
    menuPortalTarget: document.body,
    menuPosition: 'fixed',
    styles: { menuPortal: (base) => ({ ...base, zIndex: 1400 }) }
  };
  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const save = async () => {
    const DisplayName = form.DisplayName.trim();
    if (!DisplayName || saving) return;
    setSaving(true);
    try {
      await onSave({
        Title: form.Title.trim(),
        GivenName: form.GivenName.trim(),
        MiddleName: form.MiddleName.trim(),
        FamilyName: form.FamilyName.trim(),
        Suffix: form.Suffix.trim(),
        DisplayName,
        CompanyName: form.CompanyName.trim(),
        PrimaryEmailAddr: { Address: form.Email.trim() },
        PrimaryPhone: { FreeFormNumber: form.Phone.trim() },
        Mobile: { FreeFormNumber: form.Mobile.trim() },
        WebAddr: { URI: form.Website.trim() },
        CurrencyRef: form.CurrencyRef,
        BillAddr: {
          Line1: form.Line1.trim(),
          City: form.City.trim(),
          CountrySubDivisionCode: form.CountrySubDivisionCode.trim(),
          PostalCode: form.PostalCode.trim(),
          Country: form.Country.trim()
        },
        ...(form.SalesTaxRegistered && { GSTRegistrationType: 'GST' }),
        TaxIdentifier: form.TaxIdentifier.trim(),
        BillRate: form.BillRate === '' ? undefined : Number(form.BillRate),
        TermRef: form.TermRef,
        AcctNum: form.AcctNum.trim(),
        APAccountRef: form.APAccountRef
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onCancel} fullWidth maxWidth="md">
      <DialogTitle>Create supplier</DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: '#f3f4f6' }}>
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Name and contact
          </Typography>
          <TextField
            label="Currency"
            value={`${form.CurrencyRef.value} ${form.CurrencyRef.name}`}
            InputProps={{ readOnly: true }}
            sx={{ mb: 2 }}
            fullWidth
          />
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
            <TextField label="Title" value={form.Title} onChange={(e) => updateField('Title', e.target.value)} />
            <TextField
              label="First name"
              value={form.GivenName}
              onChange={(e) => updateField('GivenName', e.target.value)}
            />
            <TextField
              label="Middle name"
              value={form.MiddleName}
              onChange={(e) => updateField('MiddleName', e.target.value)}
            />
            <TextField
              label="Last name"
              value={form.FamilyName}
              onChange={(e) => updateField('FamilyName', e.target.value)}
            />
            <TextField label="Suffix" value={form.Suffix} onChange={(e) => updateField('Suffix', e.target.value)} />
            <TextField
              label="Supplier display name"
              value={form.DisplayName}
              onChange={(e) => updateField('DisplayName', e.target.value)}
              required
            />
            <TextField
              label="Company name"
              value={form.CompanyName}
              onChange={(e) => updateField('CompanyName', e.target.value)}
            />
            <TextField label="Email" value={form.Email} onChange={(e) => updateField('Email', e.target.value)} />
            <TextField label="Phone number" value={form.Phone} onChange={(e) => updateField('Phone', e.target.value)} />
            <TextField
              label="Mobile number"
              value={form.Mobile}
              onChange={(e) => updateField('Mobile', e.target.value)}
            />
            <TextField label="Website" value={form.Website} onChange={(e) => updateField('Website', e.target.value)} />
          </div>
        </Paper>

        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Address
          </Typography>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
            <TextField
              label="Street address"
              value={form.Line1}
              onChange={(e) => updateField('Line1', e.target.value)}
              className="md:tw-col-span-2"
            />
            <TextField label="City" value={form.City} onChange={(e) => updateField('City', e.target.value)} />
            <TextField
              label="State"
              value={form.CountrySubDivisionCode}
              onChange={(e) => updateField('CountrySubDivisionCode', e.target.value)}
            />
            <TextField
              label="Postcode"
              value={form.PostalCode}
              onChange={(e) => updateField('PostalCode', e.target.value)}
            />
            <TextField label="Country" value={form.Country} onChange={(e) => updateField('Country', e.target.value)} />
          </div>
        </Paper>

        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Additional info
          </Typography>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.SalesTaxRegistered}
                  onChange={(e) => updateField('SalesTaxRegistered', e.target.checked)}
                />
              }
              label="Registered for sales tax"
            />
            <TextField
              label="Australian Business Number"
              value={form.TaxIdentifier}
              onChange={(e) => updateField('TaxIdentifier', e.target.value)}
            />
            <TextField
              label="Billing rate (/hr)"
              type="number"
              value={form.BillRate}
              onChange={(e) => updateField('BillRate', e.target.value)}
              inputProps={{ step: '0.01' }}
            />
          </div>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Payments and accounting
          </Typography>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
            <div>
              <Typography variant="caption" color="text.secondary">
                Terms
              </Typography>
              <ReactSelect
                {...selectProps}
                inputId="create-vendor-terms"
                aria-label="Terms"
                isLoading={termsLoading}
                options={terms}
                value={selectedOption(terms, form.TermRef)}
                onChange={(option) => updateField('TermRef', optionToRef(option))}
                placeholder="Select QBO terms"
              />
              {termsError && (
                <Typography variant="caption" color="error">
                  {termsError}
                </Typography>
              )}
            </div>
            <TextField
              label="Account no."
              value={form.AcctNum}
              onChange={(e) => updateField('AcctNum', e.target.value)}
            />
            <div className="md:tw-col-span-2">
              <Typography variant="caption" color="text.secondary">
                Default expense category
              </Typography>
              <ReactSelect
                {...selectProps}
                inputId="create-vendor-expense-account"
                aria-label="Default expense category"
                isLoading={qboAccountLoading}
                options={accountOptions}
                value={selectedOption(accountOptions, form.APAccountRef)}
                onChange={(option) => updateField('APAccountRef', optionToRef(option))}
                placeholder="Choose account"
              />
              {qboAccountError && (
                <Typography variant="caption" color="error">
                  {qboAccountError}
                </Typography>
              )}
            </div>
          </div>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={save} variant="contained" disabled={saving || !form.DisplayName.trim()}>
          {saving ? <CircularProgress size={18} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CreateVendorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  initialName: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
};

export default CreateVendorDialog;
